import * as React from 'react'
import { Route, Routes, useParams } from 'react-router-dom'
import { driversRoutes } from './routes'
import { driversPath } from './index'
import {
  toRelativeRoute,
  wrapRoutesWithRouter,
} from '../shared/utils/routerPaths'
import { AppLayoutDahboardWithNav } from '../shared/layouts'
import { ErrorBoundary } from '../shared/components/ErrorBoundary'
import { ErrorFallback } from '../shared/components/ErrorFallback'
import { DriversProvider } from '../shared/context/drivers/DriversContext'
import { TaskProvider } from '../shared/context/TaskContext'
import { DriversLayout } from './DriverLayout'
import { driverRoutesConfig } from './routes-config'
import { OwnDriverGate } from './shared/OwnDriverGate'
import { useAccessContext } from '../shared/permissions'
import { Loader } from '@/ui'

const wrappedDriversRoutes = wrapRoutesWithRouter(driversRoutes)

// Kierowca ma dostęp tylko do tych zakładek własnego kierowcy; reszta odbija 403 na serwerze.
const DRIVER_SELF_PATHS = new Set(
  driverRoutesConfig
    .filter(
      (r) =>
        r.mode === 'single' &&
        ['activities', 'chartDetails', 'violations', 'violationsNew'].includes(
          r.id,
        ),
    )
    .map((r) => r.path),
)

const DriversModuleImpl: React.FC = () => {
  const { companyId } = useParams<{ companyId: string }>()
  const { ctx: access, isLoading: accessLoading } = useAccessContext(companyId)
  const isDriverOnly = access.isDriverOnly()

  return (
    <ErrorBoundary
      fallback={(e) => <ErrorFallback error={e.error} onRetry={e.reset} />}
    >
      <TaskProvider>
        <DriversProvider>
          <AppLayoutDahboardWithNav>
            <ErrorBoundary
              fallback={(e) => (
                <ErrorFallback error={e.error} onRetry={e.reset} />
              )}
            >
              {/* Bez znanej roli strona kierowcy zdążyłaby odpytać o cudze dane. */}
              {accessLoading ? (
                <Loader fill />
              ) : (
                <Routes>
                  {wrappedDriversRoutes.map((route) => {
                    const layout = (
                      <DriversLayout
                        companyId={companyId!}
                        viewControlBar={route.viewControlBar}
                        navigation={route.navigation}
                        heightScreen={route.heightScreen}
                        fullScreen={route.fullScreen}
                        hideSidePanel={route.hideSidePanel}
                      >
                        <route.Component />
                      </DriversLayout>
                    )
                    return (
                      <Route
                        key={route.path}
                        path={`${toRelativeRoute(route.path, driversPath)}/*`}
                        element={
                          isDriverOnly ? (
                            <OwnDriverGate companyId={companyId!}>
                              {DRIVER_SELF_PATHS.has(route.path)
                                ? layout
                                : null}
                            </OwnDriverGate>
                          ) : (
                            layout
                          )
                        }
                      />
                    )
                  })}

                  {/* fallback */}
                  <Route
                    path="*"
                    element={
                      isDriverOnly ? (
                        <OwnDriverGate companyId={companyId!} />
                      ) : (
                        <DriversLayout companyId={companyId!}>
                          <div>Select</div>
                        </DriversLayout>
                      )
                    }
                  />
                </Routes>
              )}
            </ErrorBoundary>
          </AppLayoutDahboardWithNav>
        </DriversProvider>
      </TaskProvider>
    </ErrorBoundary>
  )
}

export const DriversModule = DriversModuleImpl
